import React, { useState, useEffect } from 'react';
import axios from "axios";

const MaxAlarmsReport = ({ accessToken, clients, apiUrl }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        total: 0,
        perPage: 10,
        currentPage: 1,
        lastPage: 1
    });
    const [selectedClient, setSelectedClient] = useState('');

    // Fetch data on component mount and when filters change
    useEffect(() => {
        fetchData();
    }, [selectedClient, pagination.currentPage, pagination.perPage]);

    // Function to fetch data from API
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(apiUrl, {
                params: {
                    client_id: selectedClient,
                    page: pagination.currentPage,
                    perPage: pagination.perPage
                },
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            const result = response.data;
            setData(result.data);
            setPagination(result.pagination);
        } catch (error) {
            console.error('Error fetching data:', error);
            alert('Error fetching report data. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    // Handle client filter change
    const handleClientChange = (event) => {
        setSelectedClient(event.target.value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    // Render pagination controls
    const renderPagination = () => {
        const pages = [];
        const maxDisplayPages = 5;

        let startPage = Math.max(1, pagination.currentPage - Math.floor(maxDisplayPages / 2));
        let endPage = Math.min(pagination.lastPage, startPage + maxDisplayPages - 1);

        if (endPage - startPage + 1 < maxDisplayPages) {
            startPage = Math.max(1, endPage - maxDisplayPages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${pagination.currentPage === i ? 'active' : ''}`}>
                    <a className="page-link" onClick={() => handlePageChange(i)}>
                        {i}
                    </a>
                </li>
            );
        }

        return (
            <nav aria-label="Page navigation">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                        <a className="page-link" onClick={() => handlePageChange(Math.max(1, pagination.currentPage - 1))}>
                            Previous
                        </a>
                    </li>

                    {pages}

                    <li className={`page-item ${pagination.currentPage === pagination.lastPage ? 'disabled' : ''}`}>
                        <a className="page-link" onClick={() => handlePageChange(Math.min(pagination.lastPage, pagination.currentPage + 1))}>
                            Next
                        </a>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <>
            {/* Filter section */}
            <div className="row mb-4">
                <div className="col-md-4">
                    <div className="form-group">
                        <label>Filter by Client</label>
                        <select
                            className="form-control selectpicker"
                            value={selectedClient}
                            onChange={handleClientChange}
                        >
                            <option value="">All Clients</option>
                            {clients && clients.map(client => (
                                <option key={client.id} value={client.id}>{client.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Data table */}
            {loading ? (
                <div className="text-center my-5">
                    <div className="spinner-border text-primary"></div>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead className="text-primary">
                            <tr>
                                <th>Client</th>
                                <th>Flux Id</th>
                                <th>Flux</th>
                                <th>Date</th>
                                <th>Number of Alarms</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center">No data available</td>
                                </tr>
                            ) : (
                                data.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.client_name}</td>
                                        <td>{item.flux_id}</td>
                                        <td>{item.flux_name}</td>
                                        <td>{item.alarm_date}</td>
                                        <td>{item.alarm_count}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {!loading && data.length > 0 && renderPagination()}
        </>
    );
};

export default MaxAlarmsReport;
